import {
  ProfileTemplatesApi,
  type LeadFormFieldDto,
  type ProfileModuleDto,
  type ProfileTemplateFieldPoliciesDto,
  type ProfileTemplatePublishStatus,
} from '@up2stay/up2promo-api-client';

import {
  buildTemplateEditorSnapshot,
  extractApiErrorMessage,
  normalizeTemplateFormForSnapshot,
  sanitizeModulesForTemplateApi,
} from '@/core/lib/profile-template-editor-snapshot';
import { normalizeFieldPolicies } from '@/core/lib/profile-template-fields';
import {
  applyTemplateBrandingEntitlement,
  formToTemplateContent,
  type TemplateFormData,
} from '@/core/lib/profile-template-form';

export type TemplateDetailEditorSaveState = {
  workspaceId: string;
  templateId: string;
  canManage: boolean;
  canEditTemplate: boolean;
  name: string;
  description: string;
  policies: ProfileTemplateFieldPoliciesDto;
  form: TemplateFormData;
  modules: ProfileModuleDto[];
  teamIds: string[];
  fixedLeadFormFields: LeadFormFieldDto[];
  saveFailedLabel: string;
  /** `feature.profile_branding`; false saves branding on + default accent. */
  canUseProfileBranding: boolean;
};

export type PreparedTemplateDetailSave =
  | {
      kind: 'noop';
      clearedPendingPatch: Partial<TemplateFormData>;
    }
  | {
      kind: 'ready';
      clearedPendingPatch: Partial<TemplateFormData>;
      snapshot: string;
      workspaceId: string;
      templateId: string;
      name: string;
      description: string;
      form: TemplateFormData;
      modulesForApi: ProfileModuleDto[];
      policies: ProfileTemplateFieldPoliciesDto;
      teamIds: string[];
      fixedLeadFormFields: LeadFormFieldDto[];
      saveFailedLabel: string;
    };

export function prepareTemplateDetailSave(options: {
  state: TemplateDetailEditorSaveState;
  pendingFormPatch: Partial<TemplateFormData>;
  previousSnapshot: string | null;
}): PreparedTemplateDetailSave {
  const { state, previousSnapshot } = options;
  let pendingFormPatch = options.pendingFormPatch;

  if (
    !state.workspaceId ||
    !state.templateId ||
    !state.canManage ||
    !state.canEditTemplate ||
    !state.name.trim()
  ) {
    return { kind: 'noop', clearedPendingPatch: pendingFormPatch };
  }

  const form = normalizeTemplateFormForSnapshot(
    applyTemplateBrandingEntitlement(
      { ...state.form, ...pendingFormPatch },
      state.canUseProfileBranding,
    ),
  );
  pendingFormPatch = {};
  const modulesForApi = sanitizeModulesForTemplateApi(state.modules);
  const snapshot = buildTemplateEditorSnapshot({
    name: state.name,
    description: state.description,
    policies: state.policies,
    form,
    modules: modulesForApi,
    teamIds: state.teamIds,
  });
  if (snapshot === previousSnapshot) {
    return { kind: 'noop', clearedPendingPatch: pendingFormPatch };
  }

  return {
    kind: 'ready',
    clearedPendingPatch: pendingFormPatch,
    snapshot,
    workspaceId: state.workspaceId,
    templateId: state.templateId,
    name: state.name,
    description: state.description,
    form,
    modulesForApi,
    policies: state.policies,
    teamIds: state.teamIds,
    fixedLeadFormFields: state.fixedLeadFormFields,
    saveFailedLabel: state.saveFailedLabel,
  };
}

export async function executeTemplateDetailSave(
  prepared: Extract<PreparedTemplateDetailSave, { kind: 'ready' }>,
): Promise<
  | { kind: 'saved'; publishStatus?: ProfileTemplatePublishStatus }
  | { kind: 'error'; message: string }
> {
  try {
    const res = await ProfileTemplatesApi.update({
      path: {
        workspaceId: prepared.workspaceId,
        templateId: prepared.templateId,
      },
      body: {
        name: prepared.name.trim(),
        description: prepared.description.trim(),
        content: formToTemplateContent(
          prepared.form,
          prepared.modulesForApi,
          prepared.fixedLeadFormFields,
        ),
        fieldPolicies: normalizeFieldPolicies(prepared.policies),
        teamIds: prepared.teamIds,
      },
    });
    return {
      kind: 'saved',
      publishStatus: res.data?.publishStatus,
    };
  } catch (err) {
    console.error('Profile template save failed', err);
    return {
      kind: 'error',
      message: extractApiErrorMessage(err, prepared.saveFailedLabel),
    };
  }
}
